import { useState, useMemo, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useFetch } from '../hooks/useFetch'
import { productService } from '../services/product.service'
import { filterService } from '../services/filter.service'
import Layout from '../components/Layout/Layout'
import ProductCard from '../components/Product/ProductCard'
import { FiChevronRight as FiBreadcrumb, FiFilter } from 'react-icons/fi'

const CategoryProducts = () => {
  const { categoryName } = useParams()
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(12)
  
  // Estados para filtros
  const [colorFilter, setColorFilter] = useState('')
  const [materialFilter, setMaterialFilter] = useState('')
  const [crystalFilter, setCrystalFilter] = useState('')
  const [showMobileFilters, setShowMobileFilters] = useState(false)

  useEffect(() => {
    setCurrentPage(1)
  }, [categoryName])
  
  // Obtener productos de la categoría
  const { 
    data: productsResponse, 
    loading: productsLoading, 
    error: productsError 
  } = useFetch(
    () => {
      if (!categoryName) return Promise.resolve(null)
      
      const params = {
        page: currentPage,
        limit,
        sortBy: sortBy === 'name' ? 'name' : sortBy,
        sortOrder,
        ...(colorFilter && { color: colorFilter }),
        ...(materialFilter && { material: materialFilter }),
        ...(crystalFilter && { crystal: crystalFilter })
      }
      
      return productService.getProductsByCategory(categoryName, params)
    },
    [categoryName, currentPage, sortBy, sortOrder, colorFilter, materialFilter, crystalFilter]
  )
  
  const products = useMemo(() => {
    return productsResponse?.data?.products || []
  }, [productsResponse?.data?.products])
  
  const totalPages = productsResponse?.data?.totalPages || 1
  const totalProducts = productsResponse?.data?.total || 0
  
  // Obtener filtros desde las APIs
  const { data: colorsResponse, loading: colorsLoading } = useFetch(
    () => filterService.getColors(),
    []
  )
  
  const { data: materialsResponse, loading: materialsLoading } = useFetch(
    () => filterService.getMaterials(),
    []
  )
  
  const { data: crystalsResponse, loading: crystalsLoading } = useFetch(
    () => filterService.getCrystals(),
    []
  )
  
  // Extraer datos de las respuestas
  const availableColors = colorsResponse?.data || []
  const availableMaterials = materialsResponse?.data || []
  const availableCrystals = crystalsResponse?.data || []
  
  const handleSortChange = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split('_')
    setSortBy(newSortBy)
    setSortOrder(newSortOrder)
    setCurrentPage(1)
  }
  
  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  
  const clearFilters = () => {
    setColorFilter('')
    setMaterialFilter('')
    setCrystalFilter('')
    setCurrentPage(1)
  }
  
  // Generar números de página
  const generatePageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    
    return pages
  }
  
  const pageNumbers = generatePageNumbers()
  
  // Formatear nombre de categoría para mostrar
  const displayCategoryName = categoryName 
    ? categoryName.replace(/-/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())
    : 'Productos'
  
  return (
    <Layout>
      <div className="py-6 sm:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center text-base text-primary-gray mb-6 font-body font-extralight" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-dark hover:font-normal transition-colors">
              Inicio
            </Link>
            <FiBreadcrumb className="mx-1 sm:mx-2 flex-shrink-0" />
            Catálogo
            <FiBreadcrumb className="mx-1 sm:mx-2 flex-shrink-0" />
            <span className="font-normal">
              {displayCategoryName}
            </span>
          </nav>
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-title font-semibold text-primary-text mb-2">
              {displayCategoryName}
            </h1>
            <p className="text-primary-gray text-sm sm:text-base">
              Descubre nuestra colección de {displayCategoryName.toLowerCase()}
            </p>
          </div>
          
          {/* Filters Section */}
          <div className="mb-8">
            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="lg:hidden flex items-center gap-2 mb-4 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-primary-text hover:bg-gray-50 transition-colors"
            >
              <FiFilter className="w-4 h-4" />
              Filtros
            </button>
            
            {/* Filters */}
            <div className={`${showMobileFilters ? 'block' : 'hidden'} lg:block`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 rounded-lg">
                {/* Color Filter */}
                <div>
                  <label htmlFor="color" className="block text-sm font-medium text-primary-text mb-2">
                    Color
                  </label>
                  <select
                    id="color"
                    value={colorFilter}
                    onChange={(e) => {
                      setColorFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                    disabled={colorsLoading}
                  >
                    <option value="">{colorsLoading ? 'Cargando...' : 'Todos los colores'}</option>
                    {availableColors.map(colorItem => (
                      <option key={colorItem._id} value={colorItem.color}>{colorItem.color}</option>
                    ))}
                  </select>
                </div>
                
                {/* Material Filter */}
                <div>
                  <label htmlFor="material" className="block text-sm font-medium text-primary-text mb-2">
                    Material
                  </label>
                  <select
                    id="material"
                    value={materialFilter}
                    onChange={(e) => {
                      setMaterialFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                    disabled={materialsLoading}
                  >
                    <option value="">{materialsLoading ? 'Cargando...' : 'Todos los materiales'}</option>
                    {availableMaterials.map(materialItem => (
                      <option key={materialItem._id} value={materialItem.plated}>{materialItem.plated}</option>
                    ))}
                  </select>
                </div>
                
                {/* Crystal Filter */}
                <div>
                  <label htmlFor="crystal" className="block text-sm font-medium text-primary-text mb-2">
                    Cristal
                  </label>
                  <select
                    id="crystal"
                    value={crystalFilter}
                    onChange={(e) => {
                      setCrystalFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                    disabled={crystalsLoading}
                  >
                    <option value="">{crystalsLoading ? 'Cargando...' : 'Todos los cristales'}</option>
                    {availableCrystals.map(crystalItem => (
                      <option key={crystalItem._id} value={crystalItem.color}>{crystalItem.color}</option>
                    ))}
                  </select>
                </div>
                
                {/* Sort */}
                <div>
                  <label htmlFor="sort" className="block text-sm font-medium text-primary-text mb-2">
                    Ordenar por
                  </label>
                  <select
                    id="sort"
                    value={`${sortBy}_${sortOrder}`}
                    onChange={handleSortChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
                  >
                    <option value="name_asc">Nombre A-Z</option>
                    <option value="name_desc">Nombre Z-A</option>
                  </select>
                </div>
              </div>
              
              {/* Active Filters and Clear Button */}
              {(colorFilter || materialFilter || crystalFilter) && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {colorFilter && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-dark text-white text-xs rounded-full">
                        Color: {colorFilter}
                        <button onClick={() => setColorFilter('')} className="ml-1 hover:text-gray-300">×</button>
                      </span>
                    )}
                    {materialFilter && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-dark text-white text-xs rounded-full">
                        Material: {materialFilter}
                        <button onClick={() => setMaterialFilter('')} className="ml-1 hover:text-gray-300">×</button>
                      </span>
                    )}
                    {crystalFilter && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-dark text-white text-xs rounded-full">
                        Cristal: {crystalFilter}
                        <button onClick={() => setCrystalFilter('')} className="ml-1 hover:text-gray-300">×</button>
                      </span>
                    )}
                  </div>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-dark hover:text-primary-black transition-colors"
                  >
                    Limpiar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Results Count */}
          <div className="mb-6 flex justify-between items-center">
            {productsLoading ? (
              <div className="text-sm text-primary-gray">Mostrando...</div>
            ) : (
              <div className="text-sm text-primary-gray">
                Mostrando {products.length} de {totalProducts} productos
              </div>
            )}
            {!productsLoading && totalPages > 1 && (
              <div className="text-sm text-primary-gray">
                Página {currentPage} de {totalPages}
              </div>
            )}
          </div>
          
          {/* Products Grid */}
          <div className="mb-12">
            {productsLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="aspect-square bg-gray-200 rounded-lg mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : productsError ? (
              <div className="text-center py-12">
                <p className="text-primary-gray mb-4">Error al cargar los productos</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-black transition-colors"
                >
                  Intentar nuevamente
                </button>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-primary-gray mb-4">No se encontraron productos</p>
                <button 
                  onClick={clearFilters}
                  className="px-4 py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-black transition-colors"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    showExploreButton={false}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && !productsLoading && (
            <div className="flex justify-center items-center space-x-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
              >
                Anterior
              </button>
              
              {/* Page Numbers */}
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={currentPage === page}
                  className='px-4 py-2 text-sm border rounded-md transition-colors duration-200 border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {page}
                </button>
              ))}
              
              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
              >
                Siguiente
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default CategoryProducts